<script lang="ts" setup>
import { ref, type Ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useToast } from "vue-toastification";

import PageWrapper from "@/common/components/PageWrapper.vue";
import { type FormKitErrors } from "@/common/utils/getErrorMessage";
import {
  useDeleteRoleMutation,
  useGetPermissionsQuery,
  useGetRoleByIdQuery,
  useUpdateRoleMutation,
} from "@/roles/api/queries";
import RoleDetailsComponent from "@/roles/components/RoleDetailsComponent.vue";

import type { Permission } from "../types";

definePage({
  meta: {
    auth: ["Developer.Access"],
  },
});

const route = useRoute("/roles/[roleId]");
const errors: Ref<FormKitErrors | undefined> = ref(undefined);

const roleId: Ref<string> = ref(route.params.roleId);
const router = useRouter();
const toast = useToast();

const { mutateAsync: updateRole, isPending: isUpdatePending } =
  useUpdateRoleMutation();

const onSubmit = async (role: {
  id: string;
  permissions: string[];
  name: string;
}) => {
  await updateRole(role);
  toast.success("Successfully updated role");
};

const { mutate: deleteAsync, isPending: isDeletionPending } =
  useDeleteRoleMutation();
const { data: permissions, isPending: isPermissionsPending } =
  useGetPermissionsQuery();
const { data: currentRole, isPending: isRolePending } =
  useGetRoleByIdQuery(roleId);

const onDelete = async (id?: string) => {
  if (!id) return;

  deleteAsync(id);
  toast.success("Successfully deleted role");
  router.push("/roles");
};
</script>

<template>
  <PageWrapper
    :is-pending="isPermissionsPending || isRolePending"
    :title="currentRole?.name || 'Update role'"
    :breadcrumb-links="[
      { label: 'All roles', to: '/roles' },
      { label: 'Role details', to: '#' },
    ]"
  >
    <RoleDetailsComponent
      v-if="currentRole"
      :errors="errors"
      create-or-update="UPDATE"
      :current-role="currentRole"
      :permissions="permissions as Permission[]"
      :is-disabled="isUpdatePending || isDeletionPending"
      @submit-clicked="onSubmit"
      @delete-clicked="onDelete"
    />
  </PageWrapper>
</template>
